let _ = t => t,
  _t;
import _styled from "styled-components";
import { memo, useEffect, useCallback, useState, Fragment, useMemo } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UICardSection from 'UIComponents/card/UICardSection';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UIAlert from 'UIComponents/alert/UIAlert';
import UILink from 'UIComponents/link/UILink';
import styled from 'styled-components';
import { GYPSUM } from 'HubStyleTokens/colors';
import { isFailed, isSucceeded, isStarted } from 'conversations-async-data/async-data/operators/statusComparators';
import { getData } from 'conversations-async-data/async-data/operators/getters';
import ThreeDotIconButton from './ThreeDotIconButton';
import debounce from 'react-utils/debounce';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledUICardWrapper = styled(UICardWrapper).withConfig({
  displayName: "MessageHistory__StyledUICardWrapper",
  componentId: "sc-1qmi3cq-0"
})(["background-color:", ";box-shadow:none;margin-top:10px;padding:10px;white-space:initial;user-select:none;"], GYPSUM);
export const MessageHistoryToggleButton = ({
  historyContent,
  onClickTracker,
  containerClass,
  msgHistoryId,
  isExpandedHistoryByDefault = false
}) => {
  const [showMessageHistory, setShowMessageHistory] = useState(false);

  // Reset the history content if user moves on to another message
  useEffect(() => {
    setShowMessageHistory(isExpandedHistoryByDefault);
  }, [msgHistoryId, isExpandedHistoryByDefault]);
  const toggleHistoryContent = useMemo(() => debounce(() => {
    setShowMessageHistory(prevShowMessageHistory => {
      onClickTracker(prevShowMessageHistory);
      return !prevShowMessageHistory;
    });
  }, 300), [onClickTracker]);

  // Finds the correct container for the history based off of a provided class
  // and moves the history to the bottom of the container
  const moveHistoryToEnd = useCallback(() => {
    const referenceNode = document.querySelector('div.DraftEditor-root .public-DraftEditor-content');
    if (referenceNode) {
      const newNode = document.querySelector(`div.${containerClass}`);
      referenceNode.appendChild(newNode);
    }
  }, [containerClass]);
  useEffect(() => {
    moveHistoryToEnd();
    return () => {
      const history = document.querySelector(`div.${containerClass}`);
      if (history) history.remove();
    };
  }, [moveHistoryToEnd, containerClass]);
  return (
    /*#__PURE__*/
    // NOTE - Do not remove the extra div
    // React needs something to remove from the original parent node in the DOM
    _jsx("div", {
      children: /*#__PURE__*/_jsxs("div", {
        className: containerClass,
        contentEditable: false,
        children: [/*#__PURE__*/_jsx(ThreeDotIconButton, {
          onClickHandler: toggleHistoryContent
        }), showMessageHistory && historyContent]
      })
    })
  );
};
MessageHistoryToggleButton.displayName = 'MessageHistoryToggleButton';
const MessageHistory = /*#__PURE__*/memo(({
  fetchHistoryHtml,
  // retry function to refetch history if failed
  asyncHistoryHtml // async data record for the history fetch
}) => {
  const renderFailBanner = useCallback(() => {
    return /*#__PURE__*/_jsx(UIAlert, {
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'conversations-thread-view.editors.previousMessageHistory.fetchFailedMessage'
      }),
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: 'conversations-thread-view.editors.previousMessageHistory.fetchFailedTryAgain_jsx',
        options: {
          onClick: () => fetchHistoryHtml()
        },
        elements: {
          Link: UILink
        }
      })
    });
  }, [fetchHistoryHtml]);
  const renderMessageHistory = useCallback(() => {
    return /*#__PURE__*/_jsx(StyledUICardWrapper, {
      compact: true,
      children: /*#__PURE__*/_jsxs(_StyledUICardSection, {
        children: [isFailed(asyncHistoryHtml) && renderFailBanner(), isSucceeded(asyncHistoryHtml) && /*#__PURE__*/_jsx("div", {
          className: "message-history-content"
          /* eslint-disable-next-line react/no-danger */,
          dangerouslySetInnerHTML: {
            __html: getData(asyncHistoryHtml) || I18n.text('conversations-thread-view.editors.previousMessageHistory.noHistory')
          }
        }), isStarted(asyncHistoryHtml) && /*#__PURE__*/_jsx(UILoadingSpinner, {})]
      })
    });
  }, [renderFailBanner, asyncHistoryHtml]);
  return /*#__PURE__*/_jsx(Fragment, {
    children: renderMessageHistory()
  });
});
MessageHistory.displayName = 'MessageHistory';
export default MessageHistory;
var _StyledUICardSection = _styled(UICardSection)(_t || (_t = _`
              padding: 5;
              max-height: 200px;
              overflow: scroll;
            `));